import React from "react";
import styled from "@emotion/styled";
import Button from "~/components/button";
import {
  SearchPageWrapper,
  SearchListContent,
} from "~/components/search-page-wrapper";
import { Body1, Body2 } from "@material/react-typography";
import Card from "@material/react-card";

type ManagRefDataPageProps = {
  title: string;
  onAddNewClick: () => void;
  loading: boolean;
  /**
   * Placeholder shown above the card contents once loading settles and
   * `isEmpty` is set. Children still render (an empty list draws nothing), so
   * dialogs passed as children keep working with nothing in the list.
   */
  emptyMessage?: React.ReactNode;
  isEmpty?: boolean;
  /**
   * Whether to offer the "Add New" button at all. Pass `false` for a user who
   * may view the list but not add to it: the button is omitted rather than
   * disabled, so the page offers no affordance the user cannot use. Defaults
   * to `true`, leaving pages that do not gate creation unchanged.
   */
  canAddNew?: boolean;
};

const ManageRefDataWrapper = styled(SearchPageWrapper)`
  padding-top: 0;

  /* The MDC surface brings no padding of its own, and FormBottomRow brings
     none either, so the buttons land flush in the corner. Pad the surface so
     the fields, guidance text and buttons share one gutter. */
  .mdc-dialog__surface {
    padding: 0 8px 12px;
    width: 60vw;
  }

  .binary-picker {
    margin-left: 20px;
  }
`;

const TopItems = styled("div")`
  display: flex;
  justify-content: space-between;
`;

const EmptyMessage = styled(Body2)`
  margin: 0;
  padding: 32px 16px;
  text-align: center;
  color: rgba(0, 0, 0, 0.54);
`;

export const ManageRefDataPage: React.FC<ManagRefDataPageProps> = (props) => {
  const {
    title,
    onAddNewClick,
    loading,
    emptyMessage,
    isEmpty,
    canAddNew = true,
    children,
  } = props;

  // Only swap in the placeholder once loading has settled, so an in-flight
  // query doesn't flash "nothing here" before the first results arrive.
  const showEmpty = !loading && !!isEmpty && !!emptyMessage;

  return (
    <ManageRefDataWrapper withFilters={false}>
      <SearchListContent
        loading={loading}
        actions={
          <TopItems>
            <Body1>{title}</Body1>
            {canAddNew && (
              <Button primary onClick={onAddNewClick}>
                Add New
              </Button>
            )}
          </TopItems>
        }
      >
        <Card>
          {showEmpty && <EmptyMessage>{emptyMessage}</EmptyMessage>}
          {children}
        </Card>
      </SearchListContent>
    </ManageRefDataWrapper>
  );
};
